import Nav from "../components/Nav";
import Footer from "../components/Footer";
import styles from "./Resume.module.css";

function calcTenure(startISO, endISO) {
  const start = new Date(startISO);
  const end = endISO ? new Date(endISO) : new Date();
  const total =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());
  const yrs = Math.floor(total / 12);
  const mos = total % 12;
  const y = yrs > 0 ? `${yrs} yr${yrs > 1 ? "s" : ""}` : "";
  const m = mos > 0 ? `${mos} mo${mos > 1 ? "s" : ""}` : "";
  return [y, m].filter(Boolean).join(" ");
}

const COMPANIES = [
  {
    company: "Zapier",
    startDate: "2022-09",
    roles: [
      {
        date: "Nov 2025 – present",
        role: "Senior Applied AI Engineer",
        current: true,
        bullets: [
          "Ship LLM features at production scale across core product surfaces",
          "Build eval frameworks, benchmarking pipelines, and LLM observability systems",
          "Work on multi-step agent systems with tool use and reasoning workflows",
        ],
      },
      {
        date: "Feb 2025 – Nov 2025",
        role: "Senior Full Stack Engineer",
        bullets: [
          "Shipped LLM features at production scale in core product surface",
          "Built RAG pipelines for grounded, retrieval-based responses",
        ],
      },
      {
        date: "Aug 2024 – Feb 2025",
        role: "Full Stack Engineer",
        bullets: [
          "Transitioned into LLM feature development and applied AI systems",
          "Built early evals and observability infrastructure for LLM behavior",
        ],
      },
      {
        date: "Sep 2022 – Aug 2024",
        role: "Frontend Engineer",
        bullets: [
          "Built core features for Zapier Editor",
          "Started building LLM features and shipped one of Zapier's first AI features",
        ],
      },
    ],
  },
  {
    company: "Scanifly",
    startDate: "2021-06",
    endDate: "2022-09",
    roles: [
      {
        date: "Jun 2021 – Sep 2022",
        role: "Frontend Developer",
        bullets: [
          "Built UI system and redesigned core 3D drone-based solar modeling software",
          "Collaborated closely with CTO, CEO, and CPO on product direction and UX",
        ],
      },
      {
        date: "Feb 2021 – May 2021",
        role: "Graphic & Web Designer",
        bullets: [
          "Led redesign and development of company website and marketing presence",
          "Brand assets and marketing materials for solar design software",
        ],
      },
    ],
  },
  {
    company: "Cloud Cover Music",
    startDate: "2020-06",
    endDate: "2021-05",
    roles: [
      {
        date: "Jun 2020 – May 2021",
        role: "Web Developer (Freelance)",
        bullets: [
          "Built landing pages and email campaigns for a music streaming platform",
        ],
      },
    ],
  },
  {
    company: "Meltzer/Mandl Architects",
    startDate: "2019-07",
    endDate: "2021-05",
    roles: [
      {
        date: "Jul 2019 – May 2021",
        role: "Web Developer (Freelance)",
        bullets: [
          "Designed and built firm website and portfolio system",
          "First freelance client, referred through university program",
        ],
      },
    ],
  },
  {
    company: "NODA",
    startDate: "2018-01",
    endDate: "2021-05",
    roles: [
      {
        date: "Jan 2018 – May 2021",
        role: "Web Developer & Graphic Designer",
        bullets: [
          "Built virtual event websites during COVID for the higher education industry",
          "Redesigned company website and visual identity",
          "Designed branding and visual systems for events and campaigns",
          "Designed and produced published books",
        ],
      },
    ],
  },
];

const SKILLS = [
  "LLM feature development",
  "Evals & benchmarking",
  "Context engineering",
  "LLM observability & tracing",
  "RAG & retrieval systems",
  "Model behavior analysis",
  "Production system debugging",
  "Agentic AI systems",
  "Prompt engineering",
  "Cost optimization",
  "LLM pipeline architecture",
];

export default function Resume() {
  return (
    <div className={styles.page}>
      <Nav theme="light" />

      <div className={styles.container}>
        {/* Page hero */}
        <div className={styles.pageHero}>
          <div>
            <p className={styles.eyebrow}>// resume</p>
            <h1 className={styles.pageTitle}>
              Experience &<br />
              <em>background.</em>
            </h1>
          </div>
          {/*
          TODO: Add download button when you have an updated P
          <a
            href="/maggie-cody-resume.pdf"
            download
            className={styles.downloadBtn}
          >
            download pdf →
          </a>*/}
        </div>

        <div className={styles.mainLayout}>
          {/* Left: Experience */}
          <div>
            <p className={styles.sectionLabel}>// experience</p>
            <div className={styles.expList}>
              {COMPANIES.map((company, i) => (
                <div key={i} className={styles.companyBlock}>
                  <div className={styles.companyRow}>
                    <div className={styles.companyDot} />
                    <span className={styles.companyName}>
                      {company.company}
                    </span>
                    {company.startDate && (
                      <span className={styles.companyTenure}>
                        {calcTenure(company.startDate, company.endDate)}
                      </span>
                    )}
                  </div>
                  <div className={styles.rolesWrap}>
                    {company.roles.map((role, j) => (
                      <div key={j} className={styles.roleRow}>
                        <div
                          className={`${styles.roleDot} ${role.current ? styles.roleDotCurrent : ""}`}
                        />
                        <div>
                          <div className={styles.roleMeta}>
                            <span className={styles.roleTitle}>
                              {role.role}
                            </span>
                            {role.current && (
                              <span className={styles.expBadge}>current</span>
                            )}
                            <span className={styles.roleDate}>{role.date}</span>
                          </div>
                          {role.bullets?.length > 0 && (
                            <ul className={styles.expBullets}>
                              {role.bullets.map((b, k) => (
                                <li key={k}>{b}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Skills + Education */}
          <div>
            <p className={styles.sectionLabel}>// skills</p>
            <SkillGroup skills={SKILLS} />
            <p className={styles.sectionLabel}>// education</p>
            <div className={styles.eduRow}>
              <p className={styles.eduSchool}>University of Minnesota</p>
              <p className={styles.eduSub}>Graphic Design & Computer Science</p>
              <p className={styles.eduDate}>Aug 2016 – May 2020</p>
            </div>
          </div>
        </div>

        <Footer theme="light" />
      </div>
    </div>
  );
}

function SkillGroup({ label, skills }) {
  return (
    <div className={styles.skillGroup}>
      {label && <p className={styles.skillGroupLabel}>{label}</p>}
      <ul className={styles.skillList}>
        {skills.map((s) => (
          <li key={s} className={styles.skillItem}>
            {s}
          </li>
        ))}
      </ul>
    </div>
  );
}
